import { useState, useCallback, type FormEvent } from 'react'
import { AlertTriangle, Check, X, ChevronDown, ChevronUp, Loader2 } from 'lucide-react'
import { Button } from './ui/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card'
import { Input } from './ui/input'
import { Badge } from './ui/badge'
import { cn } from '@/lib/utils'
import type { InterruptEvent, ApprovalDecision } from '@/types/orchestrator'

interface ApprovalModalProps {
  interrupt: InterruptEvent
  onApprove: (decision: ApprovalDecision) => Promise<void>
  isProcessing?: boolean
}

/**
 * Universal modal for HITL approval workflows
 * Displays the approval question, context details, and approve/reject buttons
 */
export function ApprovalModal({ interrupt, onApprove, isProcessing = false }: ApprovalModalProps) {
  const [notes, setNotes] = useState('')
  const [isContextExpanded, setIsContextExpanded] = useState(false)
  const [modifiedParams, setModifiedParams] = useState<Record<string, string>>({})

  const { payload } = interrupt
  const { question, context, options, metadata } = payload

  const approveLabel = options?.approve_label ?? 'Approve'
  const rejectLabel = options?.reject_label ?? 'Reject'
  const allowModification = options?.allow_modification ?? false
  const modifiableParams = options?.modifiable_params ?? []
  const riskLevel = metadata?.risk_level ?? 'medium'

  const handleApprove = useCallback(
    async (e: FormEvent) => {
      e.preventDefault()
      if (isProcessing) return

      const decision: ApprovalDecision = {
        approved: true,
        notes: notes || undefined,
        modified_params: Object.keys(modifiedParams).length > 0 ? modifiedParams : undefined,
      }
      await onApprove(decision)
    },
    [notes, modifiedParams, onApprove, isProcessing]
  )

  const handleReject = useCallback(
    async (e: FormEvent) => {
      e.preventDefault()
      if (isProcessing) return

      const decision: ApprovalDecision = {
        approved: false,
        notes: notes || undefined,
      }
      await onApprove(decision)
    },
    [notes, onApprove, isProcessing]
  )

  const handleParamChange = useCallback((key: string, value: string) => {
    setModifiedParams((prev) => ({
      ...prev,
      [key]: value,
    }))
  }, [])

  const getRiskBadge = () => {
    switch (riskLevel) {
      case 'high':
        return <Badge variant="destructive">High Risk</Badge>
      case 'medium':
        return <Badge variant="secondary">Medium Risk</Badge>
      case 'low':
        return <Badge variant="outline">Low Risk</Badge>
      default:
        return null
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="approval-title"
    >
      <Card className="w-full max-w-lg mx-4 shadow-xl border-2 border-primary/20">
        <CardHeader className="pb-4">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-full bg-yellow-100 dark:bg-yellow-900/30">
              <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
            </div>
            <div className="flex-1">
              <CardTitle id="approval-title" className="text-lg">
                Approval Required
              </CardTitle>
              <CardDescription className="mt-1 flex items-center gap-2">
                {metadata?.agent_id && (
                  <span className="text-xs">Agent: {metadata.agent_id}</span>
                )}
                {getRiskBadge()}
              </CardDescription>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Question */}
          <div className="p-4 bg-muted rounded-lg">
            <p className="text-foreground font-medium">{question}</p>
          </div>

          {/* Expandable Context */}
          {Object.keys(context).length > 0 && (
            <div className="border rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setIsContextExpanded(!isContextExpanded)}
                className="w-full px-4 py-2 flex items-center justify-between text-sm text-muted-foreground hover:bg-muted/50 transition-colors"
              >
                <span>Context Details</span>
                {isContextExpanded ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </button>
              {isContextExpanded && (
                <div className="px-4 py-3 border-t bg-muted/30">
                  <pre className="text-xs overflow-auto max-h-48 whitespace-pre-wrap">
                    {JSON.stringify(context, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}

          {/* Modifiable Parameters */}
          {allowModification && modifiableParams.length > 0 && (
            <div className="space-y-3">
              <p className="text-sm font-medium text-muted-foreground">
                Modify parameters (optional):
              </p>
              {modifiableParams.map((param) => (
                <div key={param} className="flex items-center gap-2">
                  <label htmlFor={`param-${param}`} className="text-sm min-w-[100px]">
                    {param}:
                  </label>
                  <Input
                    id={`param-${param}`}
                    value={modifiedParams[param] ?? ''}
                    onChange={(e) => handleParamChange(param, e.target.value)}
                    placeholder={`Enter ${param}...`}
                    disabled={isProcessing}
                    className="flex-1"
                  />
                </div>
              ))}
            </div>
          )}

          {/* Notes */}
          <div className="space-y-2">
            <label htmlFor="approval-notes" className="text-sm font-medium text-muted-foreground">
              Notes (optional):
            </label>
            <Input
              id="approval-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add a reason or comment..."
              disabled={isProcessing}
            />
          </div>
        </CardContent>

        <CardFooter className="flex gap-3 pt-4 border-t">
          <Button
            type="button"
            variant="destructive"
            onClick={handleReject}
            disabled={isProcessing}
            className="flex-1"
          >
            {isProcessing ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <X className="h-4 w-4 mr-2" />
            )}
            {rejectLabel}
          </Button>
          <Button
            type="button"
            variant="default"
            onClick={handleApprove}
            disabled={isProcessing}
            className={cn(
              'flex-1',
              riskLevel === 'high' && 'bg-orange-600 hover:bg-orange-700'
            )}
          >
            {isProcessing ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Check className="h-4 w-4 mr-2" />
            )}
            {approveLabel}
          </Button>
        </CardFooter>
      </Card>
    </div>
  )
}
